/* CodeBlue ring personalization — snippets/cb-ring-personalize.liquid
 * Variant selection, engraving panel with live preview, hard character limits,
 * Engraving Color, and add to cart with line item properties.
 */
(() => {
  const PRODUCTION_DAYS = 21;
  // [fill, border, colorless-engraving text, solid metal tone for custom letters]
  const METAL = {
    rose:   ['#EBC9BC', '#B89486', '#7A5A4F', '#D39C8A'],
    gold:   ['#E6C9A3', '#A99478', '#6E5B40', '#D2AC5E'],
    silver: ['#E3E5E8', '#A3A7AD', '#5E636A', '#B4B8BE'],
  };
  const PAD = 8;

  class CbRingPersonalize extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-cb-config]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-cb-variants]').textContent);
      this.p = this.config.personalization || {};
      this.preview = this.p.preview || { shape: 'strip' };
      this.font = `'${this.p.webFont || 'Varela Round'}','Varela Round',sans-serif`;
      // Selected value per product option, by option index (some options render inside the panel).
      this.sel = (this.variants[0]?.options || []).map((_, i) => this.querySelector(`[data-cb-opt="${i}"].is-on`)?.dataset.cbVal);
      this.ecolor = this.querySelector('[data-cb-ecolor].is-on')?.dataset.cbEcolor || '';
      this.zones = [...this.querySelectorAll('[data-cb-zone]')];
      this.stone = this.querySelector('[data-cb-stone]');
      this.stoneVal = '';
      this.stoneMax = +(this.stone?.dataset.cbStoneMax || 1);
      this.stoneList = [];
      this.textBlock = this.querySelector('[data-cb-text-block]');
      this.notes = this.querySelector('[data-cb-notes]');
      this.uploadEl = this.querySelector('[data-cb-upload]');
      this.files = [];
      this.fontVal = '';
      this.fontMode = 'text';
      const f0 = this.querySelector('[data-cb-font].is-on');
      if (f0) this.applyFont(f0);
      this.querySelector('[data-cb-file]')?.addEventListener('change', e => this.onFiles(e.target));
      // Photo slots (lockets / frames): one photo per labelled slot, plus the B&W checkbox.
      this.slotEls = [...this.querySelectorAll('[data-cb-slot]')];
      this.slotFiles = this.slotEls.map(() => null);
      this.slotEls.forEach((el, i) => el.querySelector('[data-cb-slot-file]').addEventListener('change', e => this.onSlotFile(i, e.target)));
      // Lockets: "Add photos inside the locket" checkbox opens the slots; framed slots show the open locket half.
      this.slotsEl = this.querySelector('[data-cb-slots]');
      this.slotsToggle = this.querySelector('[data-cb-slots-toggle]');
      this.slotsToggle?.addEventListener('change', () => {
        this.slotsEl.hidden = !this.slotsToggle.checked;
        this.slotEls.forEach((_, i) => this.renderFrame(i));
        this.update();
      });
      this.frame = this.slotsEl?.dataset.cbFrame ? JSON.parse(this.slotsEl.dataset.cbFrame) : null;
      // photoSlots.frame given as a SKU key ("N_009") → the shared outlines in snippets/cb-locket-frames.liquid
      if (!this.frame && this.slotsEl?.dataset.cbFrameKey) {
        const lib = document.getElementById('cb-locket-frames');
        this.frame = lib ? JSON.parse(lib.textContent)[this.slotsEl.dataset.cbFrameKey] || null : null;
      }
      this.slotView = this.slotEls.map(() => ({ scale: 1, x: 0, y: 0 }));
      if (this.frame) this.slotEls.forEach((el, i) => this.initFrame(el, i));
      // Digital proof (owner 2026-10-24): products with no live preview offer "email me a digital proof" + an email box.
      this.proof = this.querySelector('[data-cb-proof]');
      this.proofEmail = this.querySelector('[data-cb-proof-email]');
      this.proof?.addEventListener('change', () => {
        this.proofEmail.closest('[data-cb-proof-emailwrap]').hidden = !this.proof.checked;
        this.querySelector('[data-cb-error]').hidden = true;
        if (this.proof.checked) this.proofEmail.focus({ preventScroll: true });
      });
      this.logos = this.querySelector('[data-cb-logos]');
      this.logoVal = this.querySelector('[data-cb-logo].is-on')?.dataset.cbLogo || '';
      // Product font file (webFontAsset): the inputs show the text in the piece's font too.
      if (this.p.webFontAsset) this.zones.forEach(z => { z.style.fontFamily = this.font; z.style.fontSize = '20px'; });
      this.bw = this.querySelector('[data-cb-bw]');
      this.bw?.addEventListener('change', () => { this.toggleAttribute('data-cb-bw-on', this.bw.checked); this.update(); });
      this.notes?.addEventListener('input', () => { this.querySelector('[data-cb-error]').hidden = true; this.renderFontPreview(); this.update(); });

      this.addEventListener('click', e => this.onClick(e));
      if (this.stone) {
        // The theme's own Add to cart submits the product form: block it until a stone colour is chosen.
        document.addEventListener('submit', e => {
          if (!this.isOwnForm(e.target) || this.stoneVal) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          this.stone.querySelector('[data-cb-stone-error]').hidden = false;
          this.stone.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, true);
      }
      this.zones.forEach(inp => inp.addEventListener('input', () => this.onInput(inp)));
      // A 2-line box takes one Enter (for the second line) and no more.
      this.zones.filter(z => z.dataset.cbWrap).forEach(z => z.addEventListener('keydown', e => {
        if (e.key === 'Enter' && z.value.split('\n').length >= (+z.dataset.cbLines || 2)) e.preventDefault();
      }));
      // Name boxes on the main page (owner 2026-09-30): uppercase-only / lowercase-only boxes convert what the customer
      // types or pastes instead of rejecting it; the caret stays where it was.
      this.querySelectorAll('[data-cb-case]').forEach(inp => inp.addEventListener('input', () => {
        const v = inp.dataset.cbCase === 'upper' ? inp.value.toUpperCase() : inp.value.toLowerCase();
        if (v === inp.value) return;
        const s = inp.selectionStart, e = inp.selectionEnd;
        inp.value = v;
        try { inp.setSelectionRange(s, e); } catch (_) { /* not a text input */ }
      }));
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this.close(); });
      if (!this.variant) this.resolve(0);
      this.update();
    }

    onClick(e) {
      const t = e.target.closest('button, [data-cb-close]');
      if (!t || !this.contains(t)) return;
      if (t.dataset.cbSym !== undefined) {
        e.preventDefault();
        this.insertSymbol(t.closest('.cb-ring-field').querySelector('[data-cb-zone]'), t.dataset.cbSym);
      } else if (t.dataset.cbOpt !== undefined) {
        const i = +t.dataset.cbOpt;
        this.querySelectorAll(`[data-cb-opt="${i}"]`).forEach(b => b.classList.toggle('is-on', b === t));
        this.sel[i] = t.dataset.cbVal;
        if (!this.variant) this.resolve(i);
        this.update();
      } else if (t.dataset.cbStoneRm !== undefined) {
        this.stoneList.splice(+t.dataset.cbStoneRm, 1);
        this.renderStoneList();
      } else if (t.dataset.cbStoneVal !== undefined && this.stoneMax > 1) {
        // Multi-pick (stoneColor.max, e.g. Multiple Birth Flower): each tap adds the flower to the end, in order;
        // the same one can be added twice; × removes. The order gets "Birth Flowers: March, July, July".
        if (this.stoneList.length < this.stoneMax) this.stoneList.push(t.dataset.cbStoneVal);
        this.renderStoneList();
      } else if (t.dataset.cbStoneVal !== undefined) {
        this.querySelectorAll('[data-cb-stone-val]').forEach(b => {
          b.classList.toggle('is-on', b === t);
          b.setAttribute('aria-pressed', String(b === t));
        });
        this.stoneVal = t.dataset.cbStoneVal;
        this.stone.querySelector('[data-cb-stone-name]').textContent = this.stoneVal;
        this.stone.querySelector('[data-cb-stone-error]').hidden = true;
        this.update();
      } else if (t.dataset.cbFont !== undefined) {
        this.querySelectorAll('[data-cb-font]').forEach(b => b.classList.toggle('is-on', b === t));
        this.applyFont(t);
        this.querySelector('[data-cb-error]').hidden = true;
        this.update();
      } else if (t.dataset.cbLogo !== undefined) {
        this.querySelectorAll('[data-cb-logo]').forEach(b => {
          b.classList.toggle('is-on', b === t);
          b.setAttribute('aria-pressed', String(b === t));
        });
        this.logoVal = t.dataset.cbLogo;
        this.update();
      } else if (t.dataset.cbSlotRm !== undefined) {
        this.setSlot(+t.dataset.cbSlotRm, null);
        this.update();
      } else if (t.dataset.cbFileRm !== undefined) {
        this.files.splice(+t.dataset.cbFileRm, 1);
        this.renderFiles();
        this.update();
      } else if (t.dataset.cbEcolor !== undefined) {
        this.querySelectorAll('[data-cb-ecolor]').forEach(b => b.classList.toggle('is-on', b === t));
        this.ecolor = t.dataset.cbEcolor;
        this.update();
      } else if (t.hasAttribute('data-cb-open')) this.open();
      else if (t.hasAttribute('data-cb-close')) this.close();
      else if (t.hasAttribute('data-cb-add')) this.add(t);
    }

    onInput(inp) {
      if (inp.dataset.cbWrap) { this.onWrapInput(inp); return; }
      // Hard limit: maxlength stops typing; the slice also catches paste, autofill and IME input.
      if (inp.value.length > inp.maxLength) inp.value = inp.value.slice(0, inp.maxLength);
      if (inp.hasAttribute('data-cb-upper')) inp.value = inp.value.toUpperCase();
      inp.closest('.cb-ring-field__wrap').querySelector('.cb-ring-count b').textContent = inp.value.length;
      this.querySelector('[data-cb-error]').hidden = true;
      this.update();
    }

    // 2-line boxes (B_018, owner 2026-10-24): Enter starts the second line. With a line break each line is held to
    // the per-line limit; without one the text wraps on its own (zoneLines). The count leaves out the line break.
    onWrapInput(inp) {
      const n = +inp.dataset.cbWrap, max = +inp.dataset.cbMax, maxLines = +inp.dataset.cbLines || 2;
      let v = inp.value.replace(/\r/g, '');
      if (inp.hasAttribute('data-cb-upper')) v = v.toUpperCase();
      let parts = v.split('\n').slice(0, maxLines);
      // Typing at the end of the box (owner 2026-10-24, N_033): once the last line passes the per-line limit it moves to a
      // new line in the box itself — at the last space that fits, or mid-word — so the box reads like the preview.
      const atEnd = inp.selectionStart >= inp.value.length;
      while (atEnd && parts.length < maxLines && parts[parts.length - 1].length > n) {
        const last = parts.pop();
        let cut = last.lastIndexOf(' ', n);
        if (cut <= 0) cut = n;
        parts.push(last.slice(0, cut).trimEnd(), last.slice(cut).trimStart());
      }
      if (parts.length > 1) parts = parts.map(p => p.slice(0, n));
      else parts = [parts[0].slice(0, max)];
      // The side's total limit counts letters only (line breaks are free), e.g. B_015 back: 30 over up to 4 lines.
      let room = max;
      parts = parts.map(p => { const s = p.slice(0, Math.max(0, room)); room -= s.length; return s; });
      while (parts.length > 1 && !parts[parts.length - 1] && room <= 0) parts.pop();
      v = parts.join('\n');
      if (v !== inp.value) {
        const pos = atEnd ? v.length : Math.min(inp.selectionStart, v.length);
        inp.value = v;
        try { inp.setSelectionRange(pos, pos); } catch (_) { /* ignore */ }
      }
      inp.closest('.cb-ring-field__wrap').querySelector('.cb-ring-count b').textContent = v.replace(/\n/g, '').length;
      this.querySelector('[data-cb-error]').hidden = true;
      this.update();
    }

    // Insert a symbol (e.g. ♥) at the caret; refused once the field is at its limit.
    insertSymbol(inp, sym) {
      if (inp.value.length + sym.length > inp.maxLength) {
        const err = this.querySelector('[data-cb-error]');
        err.textContent = `Maximum ${inp.maxLength} characters.`;
        err.hidden = false;
        return;
      }
      const start = inp.selectionStart ?? inp.value.length;
      const end = inp.selectionEnd ?? start;
      inp.value = inp.value.slice(0, start) + sym + inp.value.slice(end);
      this.onInput(inp);
      inp.focus({ preventScroll: true });
      inp.setSelectionRange(start + sym.length, start + sym.length);
    }

    // Font choice: a text font changes the preview/input font; an "upload" font (Handwriting) hides the
    // text box and shows the upload section instead.
    applyFont(btn) {
      this.fontVal = btn.dataset.cbFont;
      this.fontMode = btn.dataset.cbFontMode || 'text';
      if (btn.dataset.cbWebfont) this.font = `'${btn.dataset.cbWebfont}','Varela Round',sans-serif`;
      this.zones.forEach(z => {
        z.style.fontFamily = this.fontMode === 'text' ? this.font : '';
        z.style.fontSize = /Script/.test(this.font) ? '22px' : '';  // script is unreadable at 15px
      });
      this.renderFontPreview();
      if (this.textBlock) this.textBlock.hidden = this.fontMode === 'upload';
      if (this.uploadEl?.dataset.cbUploadWhen) this.uploadEl.hidden = this.fontVal !== this.uploadEl.dataset.cbUploadWhen;
    }

    // Font preview (lockets, notes.fontPreview): the engraved words from the Personalization box, large, in the chosen
    // Block / Script font. Side labels ("Front:", "Inside Left:") are dropped, as are sides listed in data-cb-skip
    // (e.g. Front = birth months → flowers) and photo lines. Hidden for My Own Handwriting or when nothing is engraved.
    renderFontPreview() {
      const box = this.querySelector('[data-cb-font-preview]');
      if (!box || !this.notes) return;
      const skip = (box.dataset.cbSkip || '').toLowerCase().split(',').map(x => x.trim()).filter(Boolean);
      const lines = this.notes.value.split('\n').map(l => {
        const m = l.match(/^\s*(front|back|inside\s*left|inside\s*right|inside|left|right)\s*(?:side)?\s*[:\-–]\s*(.*)$/i);
        if (m && skip.includes(m[1].toLowerCase().replace(/\s+/g, ' '))) return '';
        const t = (m ? m[2] : l).trim();
        return /^\(?\s*(a\s+|my\s+|the\s+)?(photo|picture|pic|image)s?\b/i.test(t) ? '' : t;
      }).filter(Boolean);
      const list = box.querySelector('[data-cb-font-preview-lines]');
      list.replaceChildren(...lines.map(t => Object.assign(document.createElement('p'), { textContent: t })));
      list.style.fontFamily = this.font;
      list.classList.toggle('is-script', /Script/.test(this.font));
      box.hidden = this.fontMode !== 'text' || !lines.length;
    }

    get textActive() { return !!this.zones.length && !(this.textBlock && this.textBlock.hidden); }
    get uploadActive() { return !!this.uploadEl && !this.uploadEl.hidden; }

    onFiles(inp) {
      const max = +this.uploadEl.dataset.cbUploadMax || 3;
      const err = this.querySelector('[data-cb-error]');
      err.hidden = true;
      for (const f of inp.files) {
        if (this.files.length >= max) { err.textContent = `You can upload up to ${max} files.`; err.hidden = false; break; }
        if (f.size > 20 * 1024 * 1024) { err.textContent = `${f.name} is larger than 20 MB.`; err.hidden = false; continue; }
        this.files.push(f);
      }
      inp.value = '';
      this.renderFiles();
      this.update();
    }

    onSlotFile(i, inp) {
      const f = inp.files[0];
      inp.value = '';
      if (!f) return;
      const err = this.querySelector('[data-cb-error]');
      err.hidden = true;
      if (f.size > 20 * 1024 * 1024) { err.textContent = `${f.name} is larger than 20 MB.`; err.hidden = false; return; }
      this.setSlot(i, f);
      this.update();
    }

    setSlot(i, f) {
      this.slotFiles[i] = f;
      const el = this.slotEls[i];
      const img = el.querySelector('[data-cb-slot-img]');
      const canShow = f && f.type.startsWith('image/') && !/heic|heif/i.test(f.type);
      if (img.src) URL.revokeObjectURL(img.src);
      img.hidden = !canShow;
      this.slotView[i] = { scale: 1, x: 0, y: 0 };
      const zoom = el.querySelector('[data-cb-slot-zoom]');
      if (zoom) { zoom.value = 1; el.querySelector('[data-cb-slot-zoomwrap]').hidden = !canShow; }
      if (canShow) {
        img.onload = () => this.renderFrame(i);
        img.src = URL.createObjectURL(f);
      } else img.removeAttribute('src');
      // HEIC can't be shown in most browsers: show the file name instead of a thumbnail.
      el.querySelector('[data-cb-slot-add]').textContent = !f ? '+ Add photo' : canShow ? '' : f.name;
      el.classList.toggle('is-filled', !!f);
      el.querySelector('[data-cb-slot-rm]').hidden = !f;
    }

    // Locket shapes (owner 2026-10-24, from the 2D CADs). Paths in a 0–1 box; the octagon (N_035 "hexagon") takes its
    // corner cuts from frame.chamfer. frame = {shape, outer:[w,h] mm, inner:[w,h] mm, bail?, chamfer?:{outer:[x,y], inner:[x,y]}}.
    shapePath(shape, part) {
      // Traced outlines (owner 2026-10-24, heart: traced from the 2D CAD so the bail joins the heart and the photo
      // follows the real compartment): frame.custom = {aspect:[w,h], outer, inner, innerBox:[x,y,w,h]} (0–1 paths).
      if (this.frame.custom?.[part]) return this.frame.custom[part];
      // innerPath: a traced photo-insert outline (N_070 cushion insert, from the CAD sheet) on a drawn locket.
      if (part === 'inner' && this.frame.innerPath) return this.frame.innerPath;
      const c = this.frame.chamfer?.[part] || [0.22, 0.18];
      return {
        oval: 'M.5 0A.5 .5 0 1 1 .5 1A.5 .5 0 1 1 .5 0Z',
        circle: 'M.5 0A.5 .5 0 1 1 .5 1A.5 .5 0 1 1 .5 0Z',
        heart: 'M.5 .12C.42 .06 .33 0 .2 0C.08 0 0 .09 0 .24C0 .36 .03 .47 .1 .58C.2 .74 .38 .9 .48 .98Q.5 1 .52 .98C.62 .9 .8 .74 .9 .58C.97 .47 1 .36 1 .24C1 .09 .92 0 .8 0C.67 0 .58 .06 .5 .12Z',
        cushion: 'M.13 .06Q.5 -.06 .87 .06Q.955 .085 .94 .13Q1.06 .5 .94 .87Q.955 .915 .87 .94Q.5 1.06 .13 .94Q.045 .915 .06 .87Q-.06 .5 .06 .13Q.045 .085 .13 .06Z',
        octagon: `M${c[0]} 0L${1 - c[0]} 0L1 ${c[1]}L1 ${1 - c[1]}L${1 - c[0]} 1L${c[0]} 1L0 ${1 - c[1]}L0 ${c[1]}Z`,
      }[shape] || 'M.5 0A.5 .5 0 1 1 .5 1A.5 .5 0 1 1 .5 0Z';
    }

    // Geometry in mm: the outer shape sits below an optional bail; the compartment is centred in the outer shape.
    frameGeom() {
      const cu = this.frame.custom;
      if (cu) {
        const [W, H] = cu.aspect, [bx, by, bw, bh2] = cu.innerBox;
        // wallBox: the compartment wall (black line) drawn around the photo, which sits inside it (red line).
        const wall = cu.wall && cu.wallBox ? [cu.wallBox[0] * W, cu.wallBox[1] * H, cu.wallBox[2] * W, cu.wallBox[3] * H] : null;
        return { ow: W, oh: H, iw: bw * W, ih: bh2 * H, bh: 0, W, H, ix: bx * W, iy: by * H, custom: true, wall };
      }
      const { outer = [13, 16], inner = [9, 12], bail = true, bailSize } = this.frame;
      // bailSize = [top width, bottom width, height] mm from the CAD; the lower ~15% tucks behind the locket.
      const bh = !bail ? 0 : bailSize ? bailSize[2] * 0.85 : outer[0] * 0.36;
      return { ow: outer[0], oh: outer[1], iw: inner[0], ih: inner[1], bh, W: outer[0], H: outer[1] + bh,
        ix: (outer[0] - inner[0]) / 2, iy: bh + (outer[1] - inner[1]) / 2 };
    }

    // Tapered bail above the locket (rounded top), in frame units scaled by k and offset to (x0, y0).
    bailPath(g, x0, y0, k) {
      const bs = this.frame.bailSize, tw = (bs ? bs[0] : g.W * 0.18) / 2, bw = (bs ? bs[1] : g.W * 0.07) / 2, cx = g.W / 2;
      const X = v => x0 + v * k, Y = v => y0 + v * k, r = tw * 0.35;
      return `M${X(cx - tw)} ${Y(r)}Q${X(cx - tw)} ${Y(0)} ${X(cx - tw + r)} ${Y(0)}L${X(cx + tw - r)} ${Y(0)}Q${X(cx + tw)} ${Y(0)} ${X(cx + tw)} ${Y(r)}`
        + `L${X(cx + bw)} ${Y(g.bh + 0.6)}L${X(cx - bw)} ${Y(g.bh + 0.6)}Z`;
    }

    // Framed slot (lockets): the box becomes the open locket half — outer shape in the metal colour, the photo
    // compartment inside it — and the photo can be dragged to position and zoomed.
    initFrame(el, i) {
      const shape = this.frame.shape || 'oval';
      const g = this.frameGeom();
      const box = el.querySelector('.cb-photo-slot__box');
      box.classList.add('cb-lk');
      box.style.aspectRatio = `${g.W}/${g.H}`;
      const NS = 'http://www.w3.org/2000/svg';
      const uid = `cb-lk-${this.dataset.productId}-${i}`;
      const tf = (x, y, w, h) => `translate(${x} ${y}) scale(${w} ${h})`;
      box.insertAdjacentHTML('afterbegin', `<svg class="cb-lk__svg" viewBox="0 0 ${g.W} ${g.H}" xmlns="${NS}" aria-hidden="true">
        <defs><clipPath id="${uid}" clipPathUnits="objectBoundingBox"><path d="${this.shapePath(shape, 'inner')}"/></clipPath></defs>
        ${g.bh ? `<path class="cb-lk__metal" d="${this.bailPath(g, 0, 0, 1)}" stroke-width="0.12"/>` : ''}
        <path class="cb-lk__metal" d="${this.shapePath(shape, 'outer')}" transform="${g.custom ? tf(0, 0, g.ow, g.oh) : tf(0.06, g.bh + 0.06, g.ow - 0.12, g.oh - 0.12)}" stroke-width="${(g.custom ? g.ow / 120 : 0.12) / g.ow}"/>
        <path class="cb-lk__line" d="${this.shapePath(shape, 'inner')}" transform="${tf(g.ix, g.iy, g.iw, g.ih)}" fill="none" stroke-width="${(g.custom ? g.ow / 150 : 0.1) / g.iw}"/>
        ${g.wall ? `<path class="cb-lk__line" d="${this.frame.custom.wall}" transform="${tf(g.wall[0], g.wall[1], g.wall[2], g.wall[3])}" fill="none" stroke-width="${g.ow / 150 / g.wall[2]}"/>` : ''}
      </svg>`);
      const comp = document.createElement('div');
      comp.className = 'cb-lk__in';
      Object.assign(comp.style, { left: `${g.ix / g.W * 100}%`, top: `${g.iy / g.H * 100}%`,
        width: `${g.iw / g.W * 100}%`, height: `${g.ih / g.H * 100}%`, clipPath: `url(#${uid})`, webkitClipPath: `url(#${uid})` });
      comp.append(el.querySelector('[data-cb-slot-img]'), el.querySelector('[data-cb-slot-add]'));
      box.append(comp);
      // Once a photo is in, clicks drag it instead of reopening the file picker (Remove clears the slot).
      box.addEventListener('click', e => { if (this.slotFiles[i]) e.preventDefault(); });
      let drag = null;
      comp.addEventListener('pointerdown', e => {
        if (!this.slotFiles[i]) return;
        e.preventDefault();
        comp.setPointerCapture(e.pointerId);
        drag = { x: e.clientX, y: e.clientY, ox: this.slotView[i].x, oy: this.slotView[i].y };
      });
      comp.addEventListener('pointermove', e => {
        if (!drag) return;
        this.slotView[i].x = drag.ox + e.clientX - drag.x;
        this.slotView[i].y = drag.oy + e.clientY - drag.y;
        this.renderFrame(i);
      });
      const end = () => { drag = null; };
      comp.addEventListener('pointerup', end);
      comp.addEventListener('pointercancel', end);
      el.querySelector('[data-cb-slot-zoom]')?.addEventListener('input', e => { this.slotView[i].scale = +e.target.value; this.renderFrame(i); });
    }

    // Cover-fit the photo in the compartment, apply zoom and offset, and keep it covering the compartment.
    renderFrame(i) {
      const el = this.slotEls[i], img = el?.querySelector('[data-cb-slot-img]');
      const comp = el?.querySelector('.cb-lk__in');
      const v = this.slotView[i];
      if (!img?.naturalWidth || !comp || !comp.clientWidth) return;
      const cw = comp.clientWidth, ch = comp.clientHeight;
      const base = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      const w = img.naturalWidth * base * v.scale, h = img.naturalHeight * base * v.scale;
      const mx = (w - cw) / 2, my = (h - ch) / 2;
      v.x = Math.max(-mx, Math.min(mx, v.x));
      v.y = Math.max(-my, Math.min(my, v.y));
      Object.assign(v, { w, h, cw, ch });
      Object.assign(img.style, { width: `${w}px`, height: `${h}px`, transform: `translate(calc(-50% + ${v.x}px), calc(-50% + ${v.y}px))` });
    }

    paintFrames() {
      if (!this.frame) return;
      const [fill, border] = this.metal();
      this.querySelectorAll('.cb-lk__metal').forEach(p => { p.setAttribute('fill', fill); p.setAttribute('stroke', border); });
      this.querySelectorAll('.cb-lk__line').forEach(p => p.setAttribute('stroke', border));
    }

    get slotsActive() { return !this.slotsToggle || this.slotsToggle.checked; }

    // The inside photos as framed, drawn as the open locket (left and right halves), for the order.
    async snapshotSlots() {
      if (!this.frame || !this.slotsActive || !this.slotCount) return null;
      const shape = this.frame.shape || 'oval';
      const g = this.frameGeom();
      // ~520 px per locket half, whatever units the frame uses (mm, or a traced outline's pixel size).
      const k = 520 / g.W, W = g.W * k, H = g.H * k, gap = 0.25 * W, pad = 0.15 * W, top = 0.22 * W;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(pad * 2 + W * 2 + gap);
      canvas.height = Math.round(top + H + pad);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#F4F4F1';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const [fill, border] = this.metal();
      const at = (d, x, y, w, h) => { const p = new Path2D(); p.addPath(new Path2D(d), new DOMMatrix([w, 0, 0, h, x, y])); return p; };
      for (let i = 0; i < Math.min(2, this.slotEls.length); i++) {
        const x0 = pad + i * (W + gap), y0 = top;
        ctx.fillStyle = '#666';
        ctx.font = `${Math.round(0.09 * W)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(this.slotEls[i].dataset.cbSlotProp.replace(/ photo$/i, '').toUpperCase(), x0 + W / 2, top * 0.6);
        ctx.fillStyle = fill;
        ctx.strokeStyle = border;
        ctx.lineWidth = 3;
        if (g.bh) {
          const b = new Path2D(this.bailPath(g, x0, y0, k));
          ctx.fill(b); ctx.stroke(b);
        }
        const o = at(this.shapePath(shape, 'outer'), x0 + 2, y0 + g.bh * k + 2, g.ow * k - 4, g.oh * k - 4);
        ctx.fill(o); ctx.stroke(o);
        const c = at(this.shapePath(shape, 'inner'), x0 + g.ix * k, y0 + g.iy * k, g.iw * k, g.ih * k);
        ctx.save();
        ctx.clip(c);
        const f = this.slotFiles[i], v = this.slotView[i];
        const img = this.slotEls[i].querySelector('[data-cb-slot-img]');
        const ix = x0 + g.ix * k, iy = y0 + g.iy * k, iw = g.iw * k, ih = g.ih * k;
        if (f && v.cw && img.naturalWidth) {
          const s = iw / v.cw;
          ctx.drawImage(img, ix + iw / 2 + (v.x - v.w / 2) * s, iy + ih / 2 + (v.y - v.h / 2) * s, v.w * s, v.h * s);
        } else {
          ctx.fillStyle = '#fff';
          ctx.fillRect(ix, iy, iw, ih);
        }
        ctx.restore();
        ctx.lineWidth = 2;
        ctx.stroke(c);
        if (g.wall) ctx.stroke(at(this.frame.custom.wall, x0 + g.wall[0] * k, y0 + g.wall[1] * k, g.wall[2] * k, g.wall[3] * k));
      }
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      return blob ? new File([blob], 'inside-photos.png', { type: 'image/png' }) : null;
    }

    get slotCount() { return this.slotsActive === false ? 0 : (this.slotFiles || []).filter(Boolean).length; }

    renderFiles() {
      const list = this.querySelector('[data-cb-file-list]');
      list.innerHTML = '';
      this.files.forEach((f, i) => {
        const li = document.createElement('li');
        if (f.type.startsWith('image/') && !/heic|heif/i.test(f.type)) {
          const img = document.createElement('img');
          img.src = URL.createObjectURL(f);
          img.alt = '';
          li.append(img);
        } else {
          const ic = document.createElement('span');
          ic.className = 'cb-ring-upload__doc';
          ic.textContent = (f.name.split('.').pop() || 'file').toUpperCase();
          li.append(ic);
        }
        const name = document.createElement('span');
        name.className = 'cb-ring-upload__name';
        name.textContent = f.name;
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.dataset.cbFileRm = i;
        rm.setAttribute('aria-label', `Remove ${f.name}`);
        rm.textContent = '×';
        li.append(name, rm);
        list.append(li);
      });
    }

    // Not every combination is a variant (Pendant Only, No Chain has only the hidden "No Chain" value): keep the value
    // just picked and move the other options to the closest variant that has it (owner 2026-09-30).
    resolve(i) {
      const best = this.variants.filter(v => v.options[i] === this.sel[i])
        .map(v => [v, v.options.filter((o, k) => o === this.sel[k]).length])
        .sort((a, b) => b[1] - a[1])[0]?.[0];
      if (!best) return;
      this.sel = [...best.options];
      this.sel.forEach((val, k) => this.querySelectorAll(`[data-cb-opt="${k}"]`)
        .forEach(b => b.classList.toggle('is-on', b.dataset.cbVal === val)));
    }

    get variant() {
      return this.variants.find(v => v.options.every((o, i) => o === this.sel[i]));
    }

    money(cents) {
      const cur = window.Shopify?.currency?.active || 'USD';
      const whole = cents % 100 === 0;
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur,
        minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(cents / 100);
    }

    // Per-option-value rules (e.g. Secret Message Ring: Thin Band = uppercase Block only; Thick Band = font
    // choice, upper + lowercase). config.personalization.styleRules = { values: { "<value>": {uppercaseOnly, fonts} } }.
    applyStyleRules() {
      const r = this.p.styleRules;
      if (!r) return;
      const rule = r.values?.[this.sel.find(v => r.values?.[v])] || {};
      this.zones.forEach(z => {
        const up = rule.uppercaseOnly ?? !!this.p.engravingZones?.find(c => c.cartPropertyName === z.dataset.cbProp)?.uppercaseOnly;
        z.toggleAttribute('data-cb-upper', up);
        if (up && z.value !== z.value.toUpperCase()) z.value = z.value.toUpperCase();
        const note = z.closest('.cb-ring-field')?.querySelector('[data-cb-upper-note]');
        if (note) note.hidden = !up;
        // Placeholder matches the letter case allowed: "ENTER YOUR ENGRAVING" vs "Enter Your Engraving".
        if (!z.hasAttribute('data-cb-fixed-placeholder') && this.preview.shape !== 'text') {
          z.placeholder = up ? 'ENTER YOUR ENGRAVING' : 'Enter Your Engraving';
        }
      });
      const btns = [...this.querySelectorAll('[data-cb-font]')];
      if (!btns.length) return;
      const allowed = rule.fonts || btns.map(b => b.dataset.cbFont);
      btns.forEach(b => { b.hidden = !allowed.includes(b.dataset.cbFont); });
      btns[0].closest('.cb-ring-field').hidden = allowed.length < 2;
      if (!allowed.includes(this.fontVal)) {
        const b = btns.find(x => x.dataset.cbFont === allowed[0]);
        btns.forEach(x => x.classList.toggle('is-on', x === b));
        this.applyFont(b);
      }
    }

    update() {
      this.applyStyleRules();
      const v = this.variant;
      const price = v ? this.money(v.price) : 'Unavailable';
      document.querySelectorAll('[data-cb-price]').forEach(el => { el.textContent = price; });
      this.sel.forEach((val, i) => { const el = this.querySelector(`[data-cb-sel="${i}"]`); if (el) el.textContent = val; });
      // Summary skips values with no button (hiddenValues, e.g. "No Chain").
      const hidden = this.p.hiddenValues || [];
      const shownSel = this.sel.filter(v => !hidden.includes(v));
      this.querySelector('[data-cb-summary]').textContent = [...shownSel, ...(this.stoneVal ? [this.stoneVal] : [])].join(' · ');
      if (v) this.syncForms(v);
      if (this.stone) this.syncStone();
      this.paintFrames();
      const add = this.querySelector('[data-cb-add]');
      if (!add) return; // pickers-only mode (no engraving panel)
      add.textContent = v ? `Add to cart — ${price}` : 'Unavailable';
      add.disabled = !v || !v.available;
      const eng = this.textActive
        ? this.zones.filter(z => z.value.trim()).map(z => `${z.dataset.cbZone.split(' (')[0]}: ${z.value.trim().replace(/\s*\n\s*/g, ' / ')}`) : [];
      const files = this.uploadActive && this.files.length ? [`${this.files.length} file${this.files.length > 1 ? 's' : ''}`] : [];
      const photos = this.slotCount ? [`${this.slotCount} photo${this.slotCount > 1 ? 's' : ''}${this.bw?.checked ? ' (B&W)' : ''}`] : [];
      this.querySelector('[data-cb-panel-sum]').textContent =
        [...shownSel, ...(this.logoVal ? [this.logoVal] : []), ...(this.fontVal ? [this.fontVal] : []), ...eng, ...files, ...photos, ...(this.ecolor ? [this.ecolor] : [])].join(' · ');
      this.drawPreview();
    }

    syncForms(v) {
      const pid = this.dataset.productId;
      document.querySelectorAll(`form[id^="product-form-main-${pid}"] input[name="id"]`).forEach(inp => {
        if (inp.value !== String(v.id)) { inp.value = v.id; inp.dispatchEvent(new Event('change', { bubbles: true })); }
      });
      const url = new URL(location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', url);
    }

    isOwnForm(form) {
      // getAttribute: the product form has an <input name="id">, which shadows form.id.
      return form instanceof HTMLFormElement
        && (form.getAttribute('id') || '').startsWith(`product-form-main-${this.dataset.productId}`);
    }

    // Stone Color rides along with the theme's product form as a line item property.
    renderStoneList() {
      const box = this.stone.querySelector('[data-cb-stone-picked]');
      const short = v => v.split(' — ')[0];
      box.replaceChildren(...this.stoneList.map((v, i) => {
        const chip = document.createElement('span');
        chip.className = 'cb-stone__chip';
        chip.innerHTML = `<b>${i + 1}</b> `;
        chip.append(short(v));
        chip.append(Object.assign(document.createElement('button'), { type: 'button', className: 'cb-stone__chip-x', textContent: '×', ariaLabel: `Remove ${short(v)}` }));
        chip.lastChild.dataset.cbStoneRm = i;
        return chip;
      }));
      this.stoneVal = this.stoneList.map(short).join(', ');
      const full = this.stoneList.length >= this.stoneMax;
      this.stone.querySelector('[data-cb-stone-name]').textContent = full
        ? `That's ${this.stoneMax} — remove one to change it.` : this.stone.dataset.cbStoneHint;
      this.stone.classList.toggle('is-full', full);
      this.stone.querySelector('[data-cb-stone-error]').hidden = true;
      this.update();
    }

    syncStone() {
      const name = `properties[${this.stone.dataset.cbStoneProp}]`;
      document.querySelectorAll(`form[id^="product-form-main-${this.dataset.productId}"]`).forEach(form => {
        let inp = form.querySelector(`input[name="${CSS.escape(name)}"]`);
        if (!inp) {
          inp = Object.assign(document.createElement('input'), { type: 'hidden', name });
          form.append(inp);
        }
        inp.value = this.stoneVal;
        inp.disabled = !this.stoneVal;
      });
      // Dynamic checkout ("Buy it now") skips the form submit, so hold it until a colour is chosen.
      document.documentElement.classList.toggle('cb-need-stone', !this.stoneVal);
    }

    metal() {
      const m = (this.sel[0] || '').toLowerCase();
      return m.includes('rose') ? METAL.rose : m.includes('gold') ? METAL.gold : METAL.silver;
    }

    drawPreview() {
      const [fill, border, ink, tone] = this.metal();
      const black = this.ecolor.startsWith('Black');
      const box = this.querySelector('[data-cb-preview]');
      box.innerHTML = '';
      // Photo-upload listings (fingerprint, paw / nose print): no preview at all (owner).
      box.hidden = this.preview.shape === 'none';
      if (box.hidden) return;
      if (!this.zones.length && this.notes) {
        // R_004: the Personalization box is the text. Block/Script → each line on a band strip in that font;
        // My Own Handwriting → the uploaded handwriting photos.
        box.classList.remove('cb-ring-preview--note');
        if (this.fontMode === 'upload') {
          const imgs = this.files.filter(f => f.type.startsWith('image/') && !/heic|heif/i.test(f.type));
          if (!imgs.length) {
            box.classList.add('cb-ring-preview--note');
            box.innerHTML = '<p>Upload photos of the handwriting below — we engrave it exactly as written, every loop and curve.</p>';
            return;
          }
          const wrap = document.createElement('div');
          wrap.className = 'cb-ring-preview__hw';
          imgs.forEach(f => { const im = document.createElement('img'); im.src = URL.createObjectURL(f); im.alt = ''; wrap.append(im); });
          box.append(wrap);
          return;
        }
        // One strip per engraved side: Front or Back → 1, Front & Back → 2 (lines tagged "Front:" / "Back:",
        // otherwise first line = front, second = back).
        const side = this.sel.find(v => /^(Front|Back)/.test(v || '')) || 'Front';
        const sides = /&/.test(side) ? ['Front', 'Back'] : [/^Back/.test(side) ? 'Back' : 'Front'];
        const raw = this.notes.value.split('\n').map(l => l.trim()).filter(Boolean);
        const tagged = { Front: [], Back: [] };
        const untagged = [];
        raw.forEach(l => {
          const m = l.match(/^(front|back)\s*[:\-–]\s*(.*)$/i);
          if (m) tagged[m[1][0].toUpperCase() + m[1].slice(1).toLowerCase()].push(m[2]);
          else untagged.push(l);
        });
        const textFor = s => {
          if (sides.length === 1) return [...tagged[s], ...untagged, ...tagged[s === 'Front' ? 'Back' : 'Front']].join(' ');
          return tagged[s].length ? tagged[s].join(' ') : (untagged[s === 'Front' ? 0 : 1] || '');
        };
        sides.forEach(s => {
          const text = textFor(s).trim();
          const item = document.createElement('div');
          item.className = 'cb-ring-preview__item';
          const lab = document.createElement('small');
          lab.textContent = s.toUpperCase();
          item.append(lab);
          const shape = document.createElement('div');
          shape.className = 'cb-ring-strip cb-ring-strip--notes';
          shape.style.background = fill;
          shape.style.borderColor = border;
          const span = document.createElement('span');
          span.textContent = text || 'Your text';
          span.style.fontFamily = this.font;
          span.dataset.base = /Script/.test(this.font) ? 24 : 16; // script faces run small — larger base size
          span.classList.toggle('cb-ring-script', /Script/.test(this.font));
          span.style.color = black ? '#111' : ink;
          span.style.letterSpacing = '.04em';
          if (!text) span.classList.add('cb-ring-empty');
          shape.append(span);
          item.append(shape);
          box.append(item);
        });
        this.fitStrip();
        return;
      }
      if (!this.textActive) {
        // Upload products (handwriting, prints): no live preview — the upload thumbnails and a proof offer.
        box.classList.add('cb-ring-preview--note');
        box.innerHTML = '<p>We engrave your handwriting, print or photo exactly as you send it.</p>'
          + '<p>Want to see it first? We can email you a digital proof for approval before we engrave.</p>';
        return;
      }
      box.classList.remove('cb-ring-preview--note');
      if (this.preview.shape === 'bar') { this.drawBar(box, fill, border, black ? '#111' : ink); return; }
      if (this.preview.shape === 'disc') { this.drawDisc(box, fill, border, black ? '#111' : ink); return; }
      if (this.preview.shape === 'tag') { this.drawTag(box, fill, border, black ? '#111' : ink); return; }
      this.zones.forEach(z => {
        const item = document.createElement('div');
        item.className = 'cb-ring-preview__item';
        const lab = document.createElement('small');
        lab.textContent = z.dataset.cbZone.toUpperCase();
        item.append(lab);
        const text = z.value || '';
        const span = document.createElement(this.preview.shape === 'text' ? 'div' : 'span');
        span.textContent = text || (this.preview.shape === 'text' ? 'ABC' : 'YOUR TEXT');
        span.style.fontFamily = this.font;
        if (/Script/.test(this.font)) { span.classList.add('cb-ring-script'); span.dataset.base = 20; }
        if (!text) span.classList.add('cb-ring-empty');
        if (this.preview.shape === 'text') {
          span.classList.add('cb-ring-letters');
          span.style.color = tone;
          item.append(span);
        } else {
          const shape = document.createElement('div');
          shape.className = this.preview.shape === 'oval' ? 'cb-ring-oval' : 'cb-ring-strip';
          shape.style.background = fill;
          shape.style.borderColor = border;
          if (this.preview.shape === 'oval') shape.style.aspectRatio = this.preview.aspect.join('/');
          span.style.color = black ? '#111' : ink;
          shape.append(span);
          item.append(shape);
        }
        box.append(item);
      });
      if (this.preview.shape === 'oval') this.fitOval();
      else if (this.preview.shape !== 'text') this.fitStrip();
    }

    // Bar tags (owner 2026-10-24, B_018 medical ID; geometry from the CAD: 30 x 6 mm bar, rounded ends, a hole near
    // each end, engraving kept inside a 0.5 mm safe line). preview = {shape:'bar', aspect:[30,6], sides:[{id, label,
    // logo?}]}; zones carry side: 'front' | 'back'; one text line per zone, auto-sized to fit the engraving area.
    drawBar(box, fill, border, ink) {
      const [aw, ah] = this.preview.aspect || [30, 6];
      const logoImg = this.querySelector('[data-cb-logo].is-on img')?.src;
      (this.preview.sides || []).forEach(side => {
        const item = document.createElement('div');
        item.className = 'cb-ring-preview__item';
        const lab = document.createElement('small');
        lab.textContent = side.label || side.id.toUpperCase();
        const bar = document.createElement('div');
        bar.className = 'cb-bar';
        bar.style.aspectRatio = `${aw}/${ah}`;
        bar.style.background = fill;
        bar.style.borderColor = border;
        bar.style.setProperty('--cb-bar-ink', ink);
        bar.innerHTML = '<span class="cb-bar__hole"></span><span class="cb-bar__hole cb-bar__hole--r"></span><div class="cb-bar__safe"></div>';
        const safe = bar.querySelector('.cb-bar__safe');
        if (side.logo && logoImg) {
          const lg = document.createElement('span');
          lg.className = 'cb-bar__logo';
          lg.style.webkitMaskImage = lg.style.maskImage = `url("${logoImg}")`;
          safe.append(lg);
        }
        const lines = document.createElement('div');
        lines.className = 'cb-bar__lines';
        const zs = this.zones.filter(z => (this.zoneCfg(z)?.side || 'front') === side.id);
        const texts = zs.flatMap(z => this.zoneLines(z));
        (texts.length ? texts : ['YOUR TEXT']).forEach(t => {
          const ln = document.createElement('span');
          ln.textContent = t;
          if (!texts.length) ln.classList.add('cb-ring-empty');
          ln.style.fontFamily = this.font;
          lines.append(ln);
        });
        safe.append(lines);
        item.append(lab, bar);
        box.append(item);
      });
      this.fitBar();
    }

    // Round charm (owner 2026-10-24, B_015 circle medical ID; 2D CAD B_015: 11 mm disc, 0.5 mm safe line, a hole at
    // each side on the centre line). Drawn in a 100-unit SVG. Front = logo in the middle + one line of text curved along
    // the bottom (like the owner's sample "TYPE 2 DIABETIC"); back = up to 4 centred lines, each kept inside the safe
    // circle and clear of the holes. preview = {shape:'disc', sides:[{id, label, logo?, curve?}]}.
    drawDisc(box, fill, border, ink) {
      const NS = 'http://www.w3.org/2000/svg';
      const logoImg = this.querySelector('[data-cb-logo].is-on img')?.src;
      (this.preview.sides || []).forEach(side => {
        const item = document.createElement('div');
        item.className = 'cb-ring-preview__item cb-disc-item';
        const lab = document.createElement('small');
        lab.textContent = side.label || side.id.toUpperCase();
        const disc = document.createElement('div');
        disc.className = 'cb-disc';
        disc.style.background = fill;
        disc.style.borderColor = border;
        disc.style.setProperty('--cb-bar-ink', ink);
        disc.innerHTML = '<span class="cb-disc__hole"></span><span class="cb-disc__hole cb-disc__hole--r"></span>';
        const zs = this.zones.filter(z => (this.zoneCfg(z)?.side || 'front') === side.id);
        const texts = zs.flatMap(z => this.zoneLines(z));
        const svg = document.createElementNS(NS, 'svg');
        svg.setAttribute('viewBox', '0 0 100 100');
        svg.classList.add('cb-disc__svg');
        disc.append(svg);
        if (side.logo && logoImg) {
          const lg = document.createElement('span');
          lg.className = 'cb-disc__logo' + (texts.length ? '' : ' cb-disc__logo--solo');
          lg.style.webkitMaskImage = lg.style.maskImage = `url("${logoImg}")`;
          disc.append(lg);
        }
        item.append(lab, disc);
        box.append(item);
        const empty = !texts.length;
        const lines = empty ? [side.curve ? 'YOUR TEXT' : 'YOUR TEXT'] : texts;
        if (side.curve) {
          // Baseline on a circle just inside the safe line, bottom half only (clear of the side holes).
          const r = 42.6;
          const path = document.createElementNS(NS, 'path');
          path.setAttribute('id', `cb-arc-${side.id}-${this.dataset.productId}`);
          path.setAttribute('d', `M ${50 - r} 50 A ${r} ${r} 0 0 0 ${50 + r} 50`);
          path.setAttribute('fill', 'none');
          const t = document.createElementNS(NS, 'text');
          t.setAttribute('font-family', this.font.replace(/'/g, ''));
          t.setAttribute('fill', ink);
          if (empty) t.setAttribute('opacity', '.35');
          const tp = document.createElementNS(NS, 'textPath');
          tp.setAttribute('href', `#${path.id}`);
          tp.setAttribute('startOffset', '50%');
          tp.setAttribute('text-anchor', 'middle');
          tp.textContent = lines.join(' ');
          t.append(tp);
          svg.append(path, t);
          // Fit along about 145° of the bottom arc (still clear of the side holes; owner wanted the front text larger).
          const avail = r * (145 * Math.PI / 180);
          t.setAttribute('font-size', '10');
          t.setAttribute('letter-spacing', '0.6');
          const w10 = t.getComputedTextLength() || 1;
          const fs = Math.min(17, 10 * avail / w10);
          t.setAttribute('font-size', fs.toFixed(2));
          t.setAttribute('letter-spacing', (0.06 * fs).toFixed(2));
          if (t.getComputedTextLength() > avail) t.setAttribute('letter-spacing', '0');
        } else {
          // Stacked lines: find the largest size at which every line fits the circle at its own height.
          const els = lines.map(txt => {
            const t = document.createElementNS(NS, 'text');
            t.setAttribute('x', '50');
            t.setAttribute('text-anchor', 'middle');
            t.setAttribute('font-family', this.font.replace(/'/g, ''));
            t.setAttribute('font-size', '10');
            t.setAttribute('fill', ink);
            if (empty) t.setAttribute('opacity', '.35');
            t.textContent = txt;
            svg.append(t);
            return t;
          });
          const w10 = els.map(t => t.getComputedTextLength() || 1);
          // Safe circle r 45.6; holes centred 41.2 from the middle, r 3.45 → lines level with them stay within ±36.25.
          const R = 45.6, holeTop = 50 - 5, holeBot = 50 + 5, holeHalf = 41.2 - 3.45 - 1.5;
          const fits = fs => {
            const lh = fs * 1.08, top0 = 50 - lh * els.length / 2;
            return els.every((t, i) => {
              const a = top0 + i * lh + fs * 0.12, b = top0 + (i + 1) * lh - fs * 0.04;
              const far = Math.max(Math.abs(a - 50), Math.abs(b - 50));
              if (far >= R) return false;
              let half = Math.sqrt(R * R - far * far) - 1.5;
              if (b > holeTop && a < holeBot) half = Math.min(half, holeHalf);
              return w10[i] * fs / 10 / 2 <= half;
            });
          };
          let lo = 2, hi = 24;
          for (let k = 0; k < 18; k++) { const mid = (lo + hi) / 2; if (fits(mid)) lo = mid; else hi = mid; }
          const fs = lo, lh = fs * 1.08, top0 = 50 - lh * els.length / 2;
          els.forEach((t, i) => {
            t.setAttribute('font-size', fs.toFixed(2));
            t.setAttribute('y', (top0 + i * lh + lh * 0.82).toFixed(2));
          });
        }
      });
    }

    // Rounded-square tag (owner 2026-10-24, N_033 medical ID necklace; 2D CAD N_033: 470 x 529 outline, hole top centre,
    // safe line inset). Front = the logo in the bottom-right corner (owner's sample); back = up to 4 centred lines below
    // the hole, sized to fill the engraving area. preview = {shape:'tag', aspect:[w,h], sides:[{id, label, logo?}]}.
    drawTag(box, fill, border, ink) {
      const NS = 'http://www.w3.org/2000/svg';
      const [aw, ah] = this.preview.aspect || [470, 529];
      const H = 100 * ah / aw;
      const logoImg = this.querySelector('[data-cb-logo].is-on img')?.src;
      (this.preview.sides || []).forEach(side => {
        const item = document.createElement('div');
        item.className = 'cb-ring-preview__item cb-disc-item';
        const lab = document.createElement('small');
        lab.textContent = side.label || side.id.toUpperCase();
        const tag = document.createElement('div');
        tag.className = 'cb-tag';
        tag.style.aspectRatio = `${aw}/${ah}`;
        tag.style.background = fill;
        tag.style.borderColor = border;
        tag.style.setProperty('--cb-bar-ink', ink);
        tag.innerHTML = '<span class="cb-tag__hole"></span>';
        const svg = document.createElementNS(NS, 'svg');
        svg.setAttribute('viewBox', `0 0 100 ${H.toFixed(2)}`);
        svg.classList.add('cb-disc__svg');
        tag.append(svg);
        if (side.logo && logoImg) {
          const lg = document.createElement('span');
          lg.className = 'cb-tag__logo';
          lg.style.webkitMaskImage = lg.style.maskImage = `url("${logoImg}")`;
          tag.append(lg);
        }
        item.append(lab, tag);
        box.append(item);
        const zs = this.zones.filter(z => (this.zoneCfg(z)?.side || 'front') === side.id);
        if (!zs.length) return;
        const texts = zs.flatMap(z => this.zoneLines(z));
        const empty = !texts.length;
        const lines = empty ? ['YOUR TEXT'] : texts;
        const els = lines.map(txt => {
          const t = document.createElementNS(NS, 'text');
          t.setAttribute('x', '50');
          t.setAttribute('text-anchor', 'middle');
          t.setAttribute('font-family', this.font.replace(/'/g, ''));
          t.setAttribute('font-size', '10');
          t.setAttribute('fill', ink);
          if (empty) t.setAttribute('opacity', '.35');
          t.textContent = txt;
          svg.append(t);
          return t;
        });
        // Engraving area: inside the safe line, below the hole.
        const left = 6, right = 94, top = 16.5, bottom = H - 6;
        const maxW = Math.max(...els.map(t => t.getComputedTextLength() || 1));
        const fs = Math.min(22, 10 * (right - left) / maxW, (bottom - top) / (els.length * 1.08));
        const lh = fs * 1.08, y0 = top + ((bottom - top) - lh * els.length) / 2;
        els.forEach((t, i) => {
          t.setAttribute('font-size', fs.toFixed(2));
          t.setAttribute('y', (y0 + i * lh + lh * 0.82).toFixed(2));
        });
      });
    }

    zoneCfg(z) { return this.p.engravingZones?.find(c => c.cartPropertyName === z.dataset.cbProp); }

    // A zone with wrap: n (owner 2026-10-24, B_018: 40 characters per side, 20 per line, 2 lines) splits into lines of
    // up to n characters: at the last space that fits, or mid-word when a word is longer than a line.
    zoneLines(z) {
      const n = this.zoneCfg(z)?.wrap;
      // The customer's own line break wins: each line as typed (held to n characters by onWrapInput).
      const typed = z.value.split('\n').map(l => l.trim().replace(/\s+/g, ' ')).filter(Boolean);
      if (n && typed.length > 1) return typed;
      const text = z.value.trim().replace(/\s+/g, ' ');
      if (!text) return [];
      if (!n || text.length <= n) return [text];
      const out = [];
      let rest = text;
      while (rest.length > n) {
        let cut = rest.lastIndexOf(' ', n);
        if (cut <= 0) cut = n;
        out.push(rest.slice(0, cut).trim());
        rest = rest.slice(cut).trim();
      }
      if (rest) out.push(rest);
      // A word break that leaves the last line too long falls back to a plain split every n characters.
      // (also when word breaks would need more lines than the side allows, e.g. 3 lines for 2).
      const maxLines = this.zoneCfg(z)?.maxLines || 2;
      return out.some(l => l.length > n) || out.length > maxLines ? text.match(new RegExp(`.{1,${n}}`, 'g')).map(s => s.trim()) : out;
    }

    // Fit each side inside its engraving area: tighten letter spacing first, then shrink the font.
    fitBar() {
      this.querySelectorAll('.cb-bar').forEach(bar => {
        const lines = bar.querySelector('.cb-bar__lines');
        const spans = [...lines.children];
        if (!spans.length) return;
        const h = lines.clientHeight, w = lines.clientWidth;
        let size = Math.min(h / spans.length * 0.86, h * 0.62);
        const set = (px, ls) => spans.forEach(s => { s.style.fontSize = `${px.toFixed(1)}px`; s.style.letterSpacing = `${ls}em`; });
        const widest = () => Math.max(...spans.map(s => s.offsetWidth));
        set(size, 0.06);
        if (widest() > w) set(size, 0.02);
        if (widest() > w) set(size, 0);
        if (widest() > w) size *= w / widest();
        set(Math.max(6, size), widest() > w ? 0 : parseFloat(spans[0].style.letterSpacing));
      });
    }

    // Short strips (fitChars): exactly as wide as a full-length engraving plus a small margin.
    fitStrip() {
      const strips = [...this.querySelectorAll('.cb-ring-strip')];
      if (!strips.length) return;
      if (this.preview.fitChars) {
        const up = this.zones.some(z => z.hasAttribute('data-cb-upper'));
        const ref = (up ? 'ABCDEFGHIJKLMNOPQRST' : 'Abcdefghijklmnopqrst').slice(0, this.preview.fitChars);
        // Size the strip for a full-length engraving at the text's own base size (Script runs larger).
        const base = +strips[0].querySelector('span')?.dataset.base || 13;
        const probe = document.createElement('span');
        probe.textContent = ref;
        probe.style.cssText = `position:absolute;visibility:hidden;font-size:${base}px;font-family:${this.font}`;
        strips[0].append(probe);
        const w = probe.offsetWidth;
        probe.remove();
        const max = strips[0].parentElement.clientWidth;
        strips.forEach(s => {
          s.style.width = `${Math.min(max, w + PAD * 2 + 2)}px`;
          s.style.minWidth = '0';
          s.style.height = base > 13 ? '38px' : '';
        });
      }
      strips.forEach(s => {
        const sp = s.querySelector('span');
        const base = +sp.dataset.base || 13;
        sp.style.fontSize = `${base}px`;
        const k = (s.clientWidth - PAD * 2) / sp.offsetWidth;
        if (k < 1) sp.style.fontSize = `${Math.max(7, base * k).toFixed(1)}px`;
      });
    }

    // Oval: scale the text to fill the face (about 80% of its width and 60% of its height).
    fitOval() {
      this.querySelectorAll('.cb-ring-oval').forEach(o => {
        const sp = o.querySelector('span');
        sp.style.fontSize = '100px';
        const k = Math.min(o.clientWidth * 0.8 / sp.offsetWidth, o.clientHeight * 0.6 / sp.offsetHeight);
        sp.style.fontSize = `${Math.max(12, Math.floor(100 * k))}px`;
      });
    }

    open() {
      this.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        this.drawPreview();
        (this.textActive ? this.zones[0] : this.notes)?.focus({ preventScroll: true });
      });
    }

    close() {
      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
    }

    validate() {
      if (this.notes?.hasAttribute('data-cb-required') && !this.notes.value.trim()) {
        return 'Please tell us what to engrave on the front and back.';
      }
      if (this.uploadActive && this.uploadEl.hasAttribute('data-cb-required') && !this.files.length) {
        return 'Please upload your photo or file.';
      }
      if (this.proof?.checked && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.proofEmail.value.trim())) {
        this.proofEmail.focus({ preventScroll: true });
        return 'Please enter the email address for your digital proof.';
      }
      const missingSlot = this.slotsActive && (this.slotEls || []).find((el, i) => el.hasAttribute('data-cb-required') && !this.slotFiles[i]);
      if (missingSlot) return `Please add the ${missingSlot.dataset.cbSlotProp.toLowerCase()}.`;
      if (!this.textActive) return '';
      const filled = this.zones.filter(z => z.value.trim());
      const required = this.zones.filter(z => z.hasAttribute('data-cb-required'));
      const missing = required.length ? required.filter(z => !z.value.trim()) : (filled.length ? [] : this.zones.slice(0, 1));
      return missing.length ? `Please enter your ${this.preview.shape === 'text' ? 'letters' : 'engraving'}.` : '';
    }

    // Preview snapshot (owner 2026-10-24): when there's a live preview with the customer's text, Add to cart redraws it
    // as a PNG and attaches it to the cart line ("Preview", a file property like the photo uploads). The preview's own
    // elements are copied into one SVG — shapes, holes, logos (as masks), text and the inner SVGs of the round/tag
    // charms — with the fonts and logo images embedded, then painted on a canvas. Returns a File, or null.
    async snapshotPreview() {
      const box = this.querySelector('[data-cb-preview]');
      if (!box || box.hidden || box.classList.contains('cb-ring-preview--note') || !box.children.length) return null;
      const B = box.getBoundingClientRect();
      if (!B.width || !B.height) return null;
      const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const fam = f => (f || '').split(',')[0].replace(/["']/g, '').trim();
      const families = new Set(), parts = [], defs = [];
      let n = 0;
      const radius = (v, w, h) => {
        const [a, b = a] = String(v).split(' ');
        const val = (s, d) => s.endsWith('%') ? parseFloat(s) / 100 * d : parseFloat(s) || 0;
        // Oversized radii shrink together, as browsers do (999px on a bar → half its height, not an ellipse).
        const rx = val(a, w), ry = val(b, h), f = Math.min(1, w / 2 / (rx || 1), h / 2 / (ry || 1));
        return [rx * f, ry * f];
      };
      for (const el of box.querySelectorAll('*')) {
        const tag = el.tagName.toLowerCase();
        if (tag !== 'svg' && el.closest('svg')) continue;
        const cs = getComputedStyle(el);
        if (cs.display === 'none' || cs.visibility === 'hidden') continue;
        const rc = el.getBoundingClientRect();
        const x = rc.left - B.left, y = rc.top - B.top, w = rc.width, h = rc.height;
        if (!w || !h) continue;
        if (tag === 'svg') {
          const c = el.cloneNode(true);
          Object.entries({ x, y, width: w, height: h }).forEach(([k, val]) => c.setAttribute(k, val));
          c.querySelectorAll('text').forEach(t => families.add(fam(t.getAttribute('font-family'))));
          parts.push(new XMLSerializer().serializeToString(c));
          continue;
        }
        const mask = cs.webkitMaskImage && cs.webkitMaskImage !== 'none' ? cs.webkitMaskImage : cs.maskImage;
        if (mask && mask !== 'none') {
          const url = mask.match(/url\(["']?([^"')]+)["']?\)/)?.[1];
          const data = url && await this.dataUrl(url);
          if (data) {
            const id = `cbm${n++}`;
            defs.push(`<mask id="${id}" maskUnits="userSpaceOnUse" style="mask-type:alpha"><image href="${data}" x="${x}" y="${y}" width="${w}" height="${h}" preserveAspectRatio="xMidYMid meet"/></mask>`);
            parts.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${cs.backgroundColor}" mask="url(#${id})"/>`);
          }
          continue;
        }
        const bw = parseFloat(cs.borderTopWidth) || 0;
        const bg = cs.backgroundColor;
        if ((bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') || bw) {
          const [rx, ry] = radius(cs.borderTopLeftRadius, w, h);
          parts.push(`<rect x="${x + bw / 2}" y="${y + bw / 2}" width="${w - bw}" height="${h - bw}" rx="${rx}" ry="${ry}" fill="${bg}"`
            + (bw ? ` stroke="${cs.borderTopColor}" stroke-width="${bw}"` : '') + '/>');
        }
        const textNode = [...el.childNodes].find(nd => nd.nodeType === 3 && nd.textContent.trim());
        if (textNode) {
          const range = document.createRange();
          range.selectNodeContents(textNode);
          const tr = range.getBoundingClientRect();
          families.add(fam(cs.fontFamily));
          parts.push(`<text x="${tr.left - B.left + tr.width / 2}" y="${tr.top - B.top + tr.height / 2}" text-anchor="middle" dominant-baseline="central"`
            + ` font-family="${esc(cs.fontFamily.replace(/"/g, "'"))}" font-size="${cs.fontSize}" font-weight="${cs.fontWeight}"`
            + ` letter-spacing="${cs.letterSpacing === 'normal' ? 0 : cs.letterSpacing}" fill="${cs.color}" opacity="${cs.opacity}">${esc(textNode.textContent.trim())}</text>`);
        }
      }
      const fonts = (await Promise.all([...families].filter(Boolean).map(f => this.fontFace(f)))).join('');
      const pad = 16, W = B.width + pad * 2, H = B.height + pad * 2;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="${-pad} ${-pad} ${W} ${H}">`
        + `<style>${fonts}</style><defs>${defs.join('')}</defs><rect x="${-pad}" y="${-pad}" width="${W}" height="${H}" fill="#F4F4F1"/>${parts.join('')}</svg>`;
      const img = new Image();
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      await img.decode();
      const k = 3, canvas = document.createElement('canvas');
      canvas.width = Math.round(W * k);
      canvas.height = Math.round(H * k);
      const ctx = canvas.getContext('2d');
      ctx.scale(k, k);
      ctx.drawImage(img, 0, 0, W, H);
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      return blob ? new File([blob], 'preview.png', { type: 'image/png' }) : null;
    }

    async dataUrl(url) {
      this._data = this._data || {};
      if (!this._data[url]) {
        this._data[url] = fetch(url).then(r => r.blob()).then(b => new Promise(res => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.readAsDataURL(b);
        })).catch(() => null);
      }
      return this._data[url];
    }

    // An @font-face rule with the font file embedded: from this page's own stylesheets (theme fonts such as Kenyan
    // Coffee), else from the Google Fonts stylesheet (Varela Round, Fredoka).
    async fontFace(family) {
      const srcs = [];
      for (const sheet of document.styleSheets) {
        let rules;
        try { rules = sheet.cssRules; } catch (_) { continue; }
        for (const r of rules || []) {
          if (r instanceof CSSFontFaceRule && fam(r.style.getPropertyValue('font-family')) === family) {
            const u = r.style.getPropertyValue('src').match(/url\(["']?([^"')]+)["']?\)/)?.[1];
            if (u) srcs.push(new URL(u, sheet.href || location.href).href);
          }
        }
      }
      if (!srcs.length) {
        // The Google Fonts stylesheet that loads this family (the page can have several, plus preconnect links).
        const link = [...document.querySelectorAll('link[href*="fonts.googleapis.com/css"]')]
          .find(l => l.href.includes(`family=${family.replace(/ /g, '+')}`));
        if (link) {
          this._gcss = this._gcss || {};
          this._gcss[link.href] = this._gcss[link.href] || fetch(link.href).then(r => r.text()).catch(() => '');
          const css = await this._gcss[link.href];
          const blocks = css.split('@font-face').filter(b => b.includes(`'${family}'`) || b.includes(`"${family}"`));
          const latin = blocks.find(b => /U\+0000-00FF/.test(b)) || blocks[blocks.length - 1];
          const u = latin?.match(/url\(([^)]+)\)/)?.[1];
          if (u) srcs.push(u);
        }
      }
      if (!srcs.length) return '';
      const data = await this.dataUrl(srcs[0]);
      return data ? `@font-face{font-family:'${family}';src:url(${data});}` : '';
      function fam(f) { return (f || '').split(',')[0].replace(/["']/g, '').trim(); }
    }

    async add(btn) {
      const err = this.querySelector('[data-cb-error]');
      const msg = this.validate();
      if (msg) { err.textContent = msg; err.hidden = false; return; }
      const v = this.variant;
      if (!v) return;

      const properties = {};
      if (this.fontVal) properties[this.p.fontChoice?.cartPropertyName || 'Font'] = this.fontVal;
      if (this.textActive) this.zones.forEach(z => {
        if (!z.value.trim()) return;
        // Wrapped zones go to the order line by line, exactly as the preview splits them.
        const lines = this.zoneCfg(z)?.wrap ? this.zoneLines(z) : [z.value.trim()];
        if (lines.length > 1) lines.forEach((l, i) => { properties[`${z.dataset.cbProp} Line ${i + 1}`] = l; });
        else properties[z.dataset.cbProp] = lines[0];
      });
      if (this.notes?.value.trim()) properties[this.notes.dataset.cbProp] = this.notes.value.trim();
      if (this.ecolor) properties[this.p.engravingColor?.cartPropertyName || 'Engraving Color'] = this.ecolor;
      if (this.stoneVal) properties[this.stone.dataset.cbStoneProp] = this.stoneVal;
      if (this.bw?.checked && this.slotCount) properties[this.bw.dataset.cbBwProp] = 'Yes';
      if (this.logoVal) properties[this.logos.dataset.cbLogoProp] = this.logoVal;
      if (this.proof?.checked) {
        properties[this.proof.dataset.cbProofProp || 'Digital Proof'] = 'Yes, email me a proof before engraving';
        properties[this.proofEmail.dataset.cbProofEmailProp || 'Proof Email'] = this.proofEmail.value.trim();
      }
      const ship = new Date(Date.now() + PRODUCTION_DAYS * 86400000);
      properties['Made to Order'] = `Ships by ${ship.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}`;

      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        let body, headers = { Accept: 'application/json' };
        const upFiles = this.uploadActive ? this.files : [];
        // Attach the preview image when it shows the customer's own text.
        const typed = (this.textActive && this.zones.some(z => z.value.trim())) || !!this.notes?.value.trim();
        let snap = null;
        if (typed && this.preview.shape !== 'none') {
          this.drawPreview();
          snap = await this.snapshotPreview().catch(e => { console.warn('[cb-ring] preview snapshot failed:', e); return null; });
        }
        const insideSnap = await this.snapshotSlots().catch(e => { console.warn('[cb-ring] inside photos snapshot failed:', e); return null; });
        if (upFiles.length || this.slotCount || snap || insideSnap) {
          // Files go up as multipart line item properties; Shopify stores them and links them on the order.
          body = new FormData();
          body.append('id', v.id);
          body.append('quantity', 1);
          Object.entries(properties).forEach(([k, val]) => body.append(`properties[${k}]`, val));
          const prefix = this.uploadEl?.dataset.cbUploadPrefix || 'Upload';
          upFiles.forEach((f, i) => body.append(`properties[${prefix} ${i + 1}]`, f, f.name));
          if (this.slotsActive) this.slotFiles.forEach((f, i) => { if (f) body.append(`properties[${this.slotEls[i].dataset.cbSlotProp}]`, f, f.name); });
          if (insideSnap) body.append(`properties[${this.p.photoSlots?.previewProperty || 'Inside Photos Preview'}]`, insideSnap, insideSnap.name);
          if (snap) body.append(`properties[${this.p.previewProperty || 'Preview'}]`, snap, snap.name);
          body.append('sections', sections.join(','));
          body.append('sections_url', location.pathname);
        } else {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify({ items: [{ id: v.id, quantity: 1, properties }], sections: sections.join(','), sections_url: location.pathname });
        }
        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', headers, body });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.description || res.status);
        }
        const json = await res.json();
        this.close();
        if (window.themeVariables?.settings?.cartType === 'page') { location.href = `${window.Shopify.routes.root}cart`; return; }
        const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        cart.sections = json.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (e) {
        console.error('[cb-ring] add to cart failed:', e);
        err.textContent = 'Something went wrong — please try again.';
        err.hidden = false;
      } finally {
        btn.disabled = false;
        this.update();
      }
    }
  }

  if (!customElements.get('cb-ring-personalize')) customElements.define('cb-ring-personalize', CbRingPersonalize);
})();
